import { ImageIcon, Link2, Pencil } from "lucide-react";
import { cn } from "../lib";
import { useEditor } from "../store";
import type { Item, Section } from "../types";

export function EditableText({
  section,
  field,
  item,
  as: Tag = "div",
  className = "",
}: {
  section: Section;
  field: string;
  item?: Item;
  as?: keyof React.JSX.IntrinsicElements;
  className?: string;
}) {
  const { editing, preview, selection, select, updateSection, updateItem } =
    useEditor();
  const enabled = editing && !preview;
  const value = String(
    item ? (item[field as keyof Item] ?? "") : (section.content[field] ?? ""),
  );
  const textStyle: React.CSSProperties = item
    ? {}
    : {
        fontSize: Number(section.content[`${field}Size`] ?? 0) || undefined,
        fontWeight: Number(section.content[`${field}Weight`] ?? 0) || undefined,
        color: String(section.content[`${field}Color`] ?? "") || undefined,
      };
  const selected =
    selection?.sectionId === section.id &&
    selection.field === field &&
    selection.itemId === item?.id;
  const commit = (text: string) =>
    item
      ? updateItem(section.id, item.id, (target) => {
          (target as unknown as Record<string, string>)[field] = text;
        })
      : updateSection(section.id, (target) => {
          target.content[field] = text;
        });
  return (
    <Tag
      className={cn(
        className,
        enabled && "cms-editable",
        selected && "cms-selected",
      )}
      contentEditable={enabled}
      suppressContentEditableWarning
      style={textStyle}
      data-placeholder="Type something…"
      onClick={(event) => {
        if (enabled) {
          event.preventDefault();
          event.stopPropagation();
          select({
            sectionId: section.id,
            kind: "text",
            field,
            itemId: item?.id,
          });
        }
      }}
      onBlur={(event) => {
        if (enabled)
          commit(event.currentTarget.textContent?.slice(0, 2000) ?? "");
      }}
      onKeyDown={(event) => {
        if (
          event.key === "Enter" &&
          !event.shiftKey &&
          ["A", "BUTTON", "SPAN"].includes(event.currentTarget.tagName)
        ) {
          event.preventDefault();
          event.currentTarget.blur();
        }
      }}
    >
      {value}
    </Tag>
  );
}

export function EditableImage({
  section,
  item,
  field = "image",
  altField = "alt",
  className = "",
  loading = "lazy",
}: {
  section: Section;
  item?: Item;
  field?: string;
  altField?: string;
  className?: string;
  loading?: "eager" | "lazy";
}) {
  const { editing, preview, selection, select } = useEditor();
  const enabled = editing && !preview;
  const src = String(item ? item.image : section.content[field]);
  const alt = String(item ? item.alt : (section.content[altField] ?? ""));
  const selected =
    selection?.sectionId === section.id &&
    selection.kind === "image" &&
    selection.itemId === item?.id;
  const focalX = Number(item?.focalX ?? section.content.focalX ?? 50),
    focalY = Number(item?.focalY ?? section.content.focalY ?? 50),
    radius = Number(item?.radius ?? section.content.imageRadius ?? 0),
    overlay = Number(item?.overlay ?? section.content.imageOverlay ?? 0);
  return (
    <div
      className={cn(
        "editable-image",
        className,
        enabled && "cms-editable",
        selected && "cms-selected",
      )}
      style={
        {
          borderRadius: radius || undefined,
          "--image-overlay": overlay / 100,
        } as React.CSSProperties
      }
      onClick={(event) => {
        if (enabled) {
          event.preventDefault();
          event.stopPropagation();
          select({
            sectionId: section.id,
            kind: "image",
            field,
            itemId: item?.id,
          });
        }
      }}
    >
      <img
        src={src}
        alt={alt}
        loading={loading}
        style={{ objectPosition: `${focalX}% ${focalY}%` }}
      />
      {overlay > 0 && (
        <i className="editable-image-overlay" aria-hidden="true" />
      )}
      {enabled && (
        <span className="image-edit-badge">
          <ImageIcon size={13} /> Replace
        </span>
      )}
    </div>
  );
}

export function EditableLink({
  section,
  field,
  children,
  className = "",
}: {
  section: Section;
  field: string;
  children: React.ReactNode;
  className?: string;
}) {
  const { editing, preview, select } = useEditor();
  const enabled = editing && !preview;
  return (
    <a
      href={enabled ? undefined : "#contact"}
      className={cn(className, enabled && "cms-editable")}
      onClick={(event) => {
        if (enabled) {
          event.preventDefault();
          event.stopPropagation();
          select({ sectionId: section.id, kind: "text", field });
        }
      }}
    >
      {children}
      {enabled && <Pencil size={12} />}
    </a>
  );
}

export function InlineHint({ type }: { type: "text" | "link" }) {
  return (
    <span className="inline-hint">
      {type === "link" ? <Link2 size={12} /> : <Pencil size={12} />} Click to
      edit
    </span>
  );
}
